import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Filter, Loader2, X } from 'lucide-react';
import { adsApi, type StreamV2KeywordOption } from '@/api/client';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function formatMatchType(value: string): string {
  const text = value.trim();
  if (!text) return '';
  return text
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function adProductLabel(value: string): string {
  if (value === 'sponsored_brands') return 'Sponsored Brands';
  if (value === 'sponsored_products') return 'Sponsored Products';
  return value;
}

function keywordFilterLabel(
  selectedIds: string[],
  known: Record<string, StreamV2KeywordOption>
): string {
  if (selectedIds.length === 0) return 'All keywords';
  if (selectedIds.length === 1) {
    return known[selectedIds[0] ?? '']?.keywordText || selectedIds[0] || '1 keyword';
  }
  return `${selectedIds.length} keywords selected`;
}

function toggleId(selectedIds: string[], id: string, checked: boolean): string[] {
  if (checked) {
    if (selectedIds.includes(id)) return selectedIds;
    return [...selectedIds, id];
  }
  return selectedIds.filter((item) => item !== id);
}

export function DaypartingKeywordFilter({
  profileId,
  campaignIds,
  selectedKeywordIds,
  onChange,
}: {
  profileId: string;
  campaignIds: string[];
  selectedKeywordIds: string[];
  onChange: (keywordIds: string[]) => void;
}) {
  const labelId = useId();
  const searchInputId = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [draftKeywordIds, setDraftKeywordIds] = useState<string[]>([]);
  const [known, setKnown] = useState<Record<string, StreamV2KeywordOption>>({});
  const debouncedSearch = useDebouncedValue(search, 250);
  const campaignKey = useMemo(() => [...campaignIds].sort().join(','), [campaignIds]);

  const keywordsQuery = useQuery({
    queryKey: ['dayparting-v2-keywords', profileId, debouncedSearch, campaignKey],
    queryFn: async () => {
      const res = await adsApi.getStreamV2Keywords(profileId, {
        q: debouncedSearch || undefined,
        campaignIds: campaignIds.length > 0 ? campaignIds : undefined,
        limit: 100,
      });
      if (!res.success) throw new Error('Failed to load keywords');
      return res.data?.keywords ?? [];
    },
    enabled: open && Boolean(profileId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const rows = keywordsQuery.data ?? [];
    if (rows.length === 0) return;
    setKnown((prev) => {
      const next = { ...prev };
      for (const row of rows) next[row.keywordId] = row;
      return next;
    });
  }, [keywordsQuery.data]);

  const visibleOptions = useMemo(() => {
    const fromApi = keywordsQuery.data ?? [];
    const seen = new Set(fromApi.map((row) => row.keywordId));
    const extras = draftKeywordIds
      .filter((id) => !seen.has(id))
      .map(
        (id) =>
          known[id] ?? {
            keywordId: id,
            keywordText: id,
            matchType: '',
            campaignId: '',
            adProduct: '',
          }
      );
    return [...extras, ...fromApi];
  }, [draftKeywordIds, keywordsQuery.data, known]);

  const activeIds = open ? draftKeywordIds : selectedKeywordIds;
  const selectedSet = useMemo(() => new Set(activeIds), [activeIds]);
  const triggerLabel = useMemo(
    () => keywordFilterLabel(selectedKeywordIds, known),
    [known, selectedKeywordIds]
  );
  const loading = keywordsQuery.isPending || keywordsQuery.isFetching;
  const isFiltered = selectedKeywordIds.length > 0;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setDraftKeywordIds(selectedKeywordIds);
      return;
    }
    setDraftKeywordIds(selectedKeywordIds);
    setSearch('');
  };

  const selectVisible = () => {
    const next = new Set(draftKeywordIds);
    const merged = [...draftKeywordIds];
    for (const row of visibleOptions) {
      if (next.has(row.keywordId)) continue;
      next.add(row.keywordId);
      merged.push(row.keywordId);
    }
    setDraftKeywordIds(merged);
  };

  const applyDraft = () => {
    onChange(draftKeywordIds);
    setOpen(false);
    setSearch('');
  };

  return (
    <div
      className="rounded-xl border border-border bg-surface p-3 shadow-sm sm:p-4"
      role="group"
      aria-labelledby={labelId}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-1">
          <p id={labelId} className="text-sm font-semibold text-foreground">
            Keyword filter
          </p>
          <p className="text-xs text-foreground-subtle">
            Limit the heatmap and summary cards to Sponsored Products and Sponsored Brands
            keywords. Leave unselected to include every keyword.
          </p>
        </div>

        <PopoverTriggerButton
          labelId={labelId}
          profileId={profileId}
          open={open}
          isFiltered={isFiltered}
          loading={loading && open}
          triggerLabel={triggerLabel}
          onOpenChange={handleOpenChange}
        >
          <div className="space-y-3 border-b border-border px-3 py-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-foreground">Select keywords</p>
                <p className="text-xs text-foreground-subtle">
                  Search by keyword text or ID. Check one or more keywords.
                </p>
              </div>
              {draftKeywordIds.length > 0 ? (
                <button
                  type="button"
                  className="shrink-0 text-xs font-medium text-foreground-muted hover:text-foreground"
                  onClick={() => setDraftKeywordIds([])}
                >
                  Clear all
                </button>
              ) : null}
            </div>
            <div>
              <label htmlFor={searchInputId} className="sr-only">
                Search keywords
              </label>
              <input
                id={searchInputId}
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search keyword text or ID"
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                autoFocus
              />
            </div>
            {visibleOptions.length > 0 ? (
              <button
                type="button"
                className="text-xs font-medium text-brand-deep hover:underline"
                onClick={selectVisible}
              >
                Select all matching ({visibleOptions.length})
              </button>
            ) : null}
          </div>

          <div className="max-h-64 overflow-y-auto px-1 py-1" role="listbox" aria-multiselectable="true">
            {keywordsQuery.isError ? (
              <p className="px-3 py-4 text-sm text-error">Failed to load keywords.</p>
            ) : loading && visibleOptions.length === 0 ? (
              <p className="px-3 py-4 text-sm text-foreground-muted">Loading keywords…</p>
            ) : visibleOptions.length === 0 ? (
              <p className="px-3 py-4 text-sm text-foreground-muted">No keywords match.</p>
            ) : (
              <ul className="m-0 list-none p-0">
                {visibleOptions.map((row) => {
                  const checked = selectedSet.has(row.keywordId);
                  const matchType = formatMatchType(row.matchType);
                  return (
                    <li key={row.keywordId} role="option" aria-selected={checked}>
                      <label
                        className={cn(
                          'flex cursor-pointer items-start gap-3 rounded-lg px-2.5 py-2.5 text-sm',
                          checked ? 'bg-brand-subtle/50' : 'hover:bg-surface-muted'
                        )}
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 rounded border-border text-brand focus:ring-brand/30"
                          checked={checked}
                          onChange={(event) =>
                            setDraftKeywordIds(
                              toggleId(draftKeywordIds, row.keywordId, event.target.checked)
                            )
                          }
                        />
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-foreground">
                            {row.keywordText || row.keywordId}
                          </span>
                          <span className="block truncate text-xs text-foreground-muted">
                            {[matchType, adProductLabel(row.adProduct), row.keywordId]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2.5">
            <p className="text-xs text-foreground-subtle">
              {draftKeywordIds.length > 0
                ? `${draftKeywordIds.length} keyword${draftKeywordIds.length === 1 ? '' : 's'} selected`
                : 'All keywords included'}
            </p>
            <Button variant="secondary" size="sm" onClick={applyDraft}>
              Done
            </Button>
          </div>
        </PopoverTriggerButton>
      </div>

      {isFiltered ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border/70 pt-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-foreground-subtle">
            Active
          </span>
          {selectedKeywordIds.map((keywordId) => {
            const label = known[keywordId]?.keywordText || keywordId;
            const matchType = formatMatchType(known[keywordId]?.matchType ?? '');
            return (
              <span
                key={keywordId}
                className="inline-flex max-w-full items-center gap-1 rounded-full border border-brand/30 bg-brand-subtle/60 px-2.5 py-1 text-xs text-foreground"
              >
                <span className="truncate">
                  {label}
                  {matchType ? ` · ${matchType}` : ''}
                </span>
                <button
                  type="button"
                  className="shrink-0 rounded-full text-foreground-muted hover:text-foreground"
                  aria-label={`Remove ${label}`}
                  onClick={() => onChange(toggleId(selectedKeywordIds, keywordId, false))}
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              </span>
            );
          })}
          <button
            type="button"
            className="text-xs font-medium text-foreground-muted hover:text-foreground"
            onClick={() => onChange([])}
          >
            Clear all
          </button>
        </div>
      ) : null}
    </div>
  );
}

function PopoverTriggerButton({
  labelId,
  profileId,
  open,
  isFiltered,
  loading,
  triggerLabel,
  onOpenChange,
  children,
}: {
  labelId: string;
  profileId: string;
  open: boolean;
  isFiltered: boolean;
  loading: boolean;
  triggerLabel: string;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}) {
  return (
    <FilterPopover open={open} onOpenChange={onOpenChange} panel={children}>
      <button
        type="button"
        disabled={!profileId}
        aria-labelledby={labelId}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'inline-flex w-full min-w-[14rem] max-w-full items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-sm lg:w-auto lg:min-w-[18rem]',
          'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
          isFiltered
            ? 'border-brand/40 bg-brand-subtle/40 text-foreground hover:bg-brand-subtle/60'
            : 'border-border bg-page text-foreground hover:bg-page/80'
        )}
      >
        <span className="inline-flex min-w-0 items-center gap-2">
          <Filter
            className={cn('h-4 w-4 shrink-0', isFiltered ? 'text-brand-deep' : 'text-foreground-muted')}
            aria-hidden
          />
          <span className="truncate text-left">{triggerLabel}</span>
        </span>
        {loading ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand" aria-hidden />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-foreground-muted" aria-hidden />
        )}
      </button>
    </FilterPopover>
  );
}

function FilterPopover({
  open,
  onOpenChange,
  panel,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  panel: ReactNode;
  children: ReactNode;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" className="w-[min(26rem,calc(100vw-2rem))] p-0">
        {panel}
      </PopoverContent>
    </Popover>
  );
}
